import React, { useState, useEffect } from 'react';
import client from '../api'

import { FullPageSpinner } from 'pages/full-page-spinner'

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID
const COGNITO_REDIRECT_URI = import.meta.env.VITE_COGNITO_REDIRECT_URI

interface LoginData {
    username: string,
    password: string
}

interface SsoLoginData {
    email: string,
}

type User = {
    id: number,
    ixUserId: number,
    user: string,
    password: string,
    firstName: string,
    lastName: string,
    role: string,
    klasse: string,
    ncremUserId: number | null
}

export type ContextProps = {
    user: User | null,
    error: string | null,
    login: (data: LoginData) => Promise<any>,
    ssoLogin: (data: SsoLoginData) => void,
    getSsoToken: (code: string) => Promise<any>,
    register: () => void,
    logout: (redirectFn?: () => void) => void
}

/* v8 ignore start */
const defaultProps: ContextProps = {
    user: null,
    error: null,
    login: async () => null,
    ssoLogin: () => null,
    getSsoToken: async () => null,
    register: () => null,
    logout: () => null,
}
/* v8 ignore end */

const IxAuthContext = React.createContext<ContextProps>(defaultProps)

function IxAuthProvider(props: any) {
    const [data, setData] = useState<any>({
        user: null,
        error: null,
        status: 'idle'
    })

    const isLoading = data.status === 'idle' || data.status === 'loading'

    const handleUserDataResolve = (data: any, details: any) => {
        // set user role since it comes without one from backend right now
        /* v8 ignore start */
        if (data.user === 'admin') {
            data.role = 'admin'
        } else {
            data.role = 'user'
        }
        /* v8 ignore end */

        // remap id property

        const userInfo = data
        setData({
            status: 'done',
            user: {
                id: userInfo.user_id,
                user: userInfo.user,
                role: userInfo.user === 'admin' || userInfo.user === 'mmv' ? 'admin' : 'user',
                firstName: details.VORNAME || userInfo.user || 'user',
                lastName: details.NACHNAME || 'test',
                ixUserId: userInfo.ix_user_id,
                klasse: details.KLASSE || 'A',
                ncremUserId: details.NCREM_USER_ID,
            },
            error: null
        })
    }

    const handleUserDataReject = (data: any) => {
        if (data.response && data.response.status === 401) {
            localStorage.removeItem('token')
            window.location.assign(String(window.location))
        }

        setData({
            status: 'error',
            user: null,
            error: data.message
        })
    }

    useEffect(() => {
        // try to get token from localStorage
        const token = localStorage.getItem('token')

        if (token) {
            setData({
                ...data,
                status: 'loading'
            })
            client.defaults.headers.common['Authorization'] = `Token ${token}`

            const getUser = async () => {
                try {
                    const userInfoRes = await client.get('/api/ixweb/user_info/')
                    const userDetailsRes = await client.get(`/api/ixweb/userdetails/`, { params: { action: 'eql', user_id: userInfoRes.data.ix_user_id, fields: 'username,user_id,klasse,vorname,nachname,ncrem_user_id' } })

                    handleUserDataResolve(userInfoRes.data, userDetailsRes.data[0])
                } catch (e) {
                    handleUserDataReject(e)
                }
            }
            getUser()
        } else {
            setData({
                ...data,
                user: null,
                status: 'done'
            })
        }
    }, []) // eslint-disable-line

    const login = (data: LoginData): Promise<any> => {
        setData({
            user: null,
            error: null,
            status: 'loading'
        })
        return new Promise<void>((resolve, _) => {
            client.post('/api/ixweb/ix-signin/', data, { headers: { Authorization: null } })
                .then(async (data: any) => {
                    window.localStorage.setItem('token', data.data.token)  // store in localStorage
                    client.defaults.headers.common['Authorization'] = `Token ${data.data.token}`

                    const userId = data.data.user?.ix_user_id
                    try {
                        const userInfoRes = await client.get('/api/ixweb/user_info/')
                        const userDetailsRes = await client.get(`/api/ixweb/userdetails/`, { params: { action: 'eql', user_id: userId, fields: 'username,user_id,klasse,vorname,nachname,ncrem_user_id' } })

                        handleUserDataResolve(userInfoRes.data, userDetailsRes.data[0])
                    } catch (e) {
                        handleUserDataReject(e)
                    }


                    // .then((userData: any) => { handleUserDataResolve(userData, data.data.user.ix_user_id) }, handleUserDataReject)
                    // .then(() => resolve())
                }, (e) => {
                    console.log('e.response.data', e.response.data)
                    setData({
                        user: null,
                        error: e.response.data.messages.error[0].key,
                        status: 'error'
                    })
                    resolve()
                })
        })
    }

    /* v8 ignore next */
    const register = () => {}

    const logout = (redirectFn?: () => void) => {
        window.localStorage.removeItem('token')

        delete client.defaults.headers.common['Authorization']
        setData({
            user: null,
            error: null,
            status: 'done'
        })

        redirectFn && redirectFn()
    }

    const ssoLogin = (data: SsoLoginData) => {
        setData({
            user: null,
            error: null,
            status: 'loading'
        })

        client.get(`/api/ixweb/sso/get-sso-provider/?email=${data.email}`).then(res => {
            const idpIdentifier = res.data

            const cognitoLoginUrl = `https://${COGNITO_DOMAIN}/oauth2/authorize?response_type=code&client_id=${COGNITO_CLIENT_ID}&redirect_uri=${COGNITO_REDIRECT_URI}&scope=openid%20profile%20email%20aws.cognito.signin.user.admin&identity_provider=${idpIdentifier}`
            window.location.href = cognitoLoginUrl
        })

    }

    const getSsoToken = (code: string) => {
        return new Promise<void>((resolve, reject) => {
            client.post('/api/ixweb/sso/ix-sso-auth/', { code }).then(async (res: any) => {
                if (res.data.token === undefined) {
                    setData({ user: null, error: 'Login failed', status: 'error' })
                    reject()
                    return
                }
                window.localStorage.setItem('token', res.data.token)
                client.defaults.headers.common['Authorization'] = `Token ${res.data.token}`

                try {
                    const userId = res.data.user.ix_user_id
                    const userInfoRes = await client.get('/api/ixweb/user_info/')
                    const userDetailsRes = await client.get(`/api/ixweb/userdetails/`, { params: { action: 'eql', user_id: userId, fields: 'username,user_id,klasse,vorname,nachname,ncrem_user_id' } })

                    handleUserDataResolve(userInfoRes.data, userDetailsRes.data[0])
                    resolve()
                } catch (e) {
                    handleUserDataReject(e)
                    reject()
                }
            }).catch((e) => {
                setData({ user: null, error: 'Login failed', status: 'error' })
                reject()
            })
        })
    }

    return (
        <IxAuthContext.Provider value={{user: data.user, error: data.error, login, register, logout, ssoLogin, getSsoToken}} {...props}>
            {isLoading ? (
                <FullPageSpinner />
            ) : (
                props.children
            )}
        </IxAuthContext.Provider>
    )
}

/* v8 ignore next */
const useIxAuth = () => React.useContext(IxAuthContext)

export {
    IxAuthProvider,
    useIxAuth
}
